import styled from 'styled-components';
import { RootState } from 'store';
import { toast } from 'react-toastify';
import { useFormContext } from 'react-hook-form';
import { useParams } from 'react-router';
import { useSelector } from 'react-redux';
import { useEffect, useState } from 'react';

import { OrderPageParams } from 'order/interfaces/OrderPageParams';
import { OrderStatusEnums } from 'order/enums/orderEnums';

import { CurtainTabsEnum } from 'curtain/enums/CurtainTabsEnum';
import { setCurtainTab } from 'curtain/store/curtainActions';

import UtilService from 'shared/services/util.service';
import { ButtonPrimary, ButtonSecondary } from 'shared/components/Button';
import { ConfirmationModal } from 'shared/components/ConfirmationModal';
import { Modal } from 'shared/components/Modal';
import { ModalWrapper } from 'shared/components/ModalWrapper';
import { Spacer } from 'shared/components/Layout';
import { Wrapper } from 'shared/components/Wrapper';
import { lynch, anzac } from 'shared/config/Colors';
import { useAppDispatch } from 'shared/hooks/useAppDispatch';
import { useHasPermissions } from 'shared/hooks/useHasPermissions';
import { userPermissionsValues } from 'shared/enum/userPermissionsEnum';

import { testId } from 'tests/utils';

import {
  ACKTestButtonEnum,
  ACKTestContainerEnum,
} from 'tests/enums/ACKTestEnums';

import { AckConsentForm } from '../Acknowledgment';
import { SpecialOrderApprovalStatusEnum } from '../enums/SpecialOrderApprovalStatusEnum';
import SpecialOrderApprovalModal from './SpecialOrderApprovalModal';

import {
  approveSpecialOrderApproval,
  disapproveSpecialOrderApproval,
} from '../store/ACKActions';

const DealerSORActionsContainer = styled(Wrapper)``;

const DealerSORActions = () => {
  const dispatch = useAppDispatch();
  const methodsContext = useFormContext<AckConsentForm>();

  const { orderId } = useParams<OrderPageParams>();

  const order = useSelector((state: RootState) => state.orderReducer.order);

  const [approveSORModalOpened, setApproveSORModalOpened] = useState(false);
  const [requestChangesSORModalOpened, setRequestChangesSORModalOpened] =
    useState(false);

  const specialOrderApprovals = order?.specialOrderApprovals ?? [];

  const pendingApprovals = specialOrderApprovals.filter(
    (soa) => soa.status === SpecialOrderApprovalStatusEnum.PENDING
  );

  const canApproveOrDisapproveOnBehalfOfDealer = useHasPermissions(
    userPermissionsValues.CSR_VIEW_ORDERBOARD
  );

  const isUserDealerOrSalesRep = useHasPermissions([
    userPermissionsValues.DEALER_VIEW_ORDERBOARD,
    userPermissionsValues.SALES_REPRESENTATIVE_VIEW_ORDER_BOARD,
  ]);

  const getCheckedApprovalIds = (): string[] => {
    const consents = methodsContext.getValues('specialOrderApprovalConsents');
    return pendingApprovals
      .filter((soa) => consents?.[soa.id])
      .map((soa) => soa.id);
  };

  const checkedCount = methodsContext.watch('specialOrderApprovalConsents');
  const isPlural = Object.values(checkedCount ?? {}).filter(Boolean).length > 1;

  const validateAtLeastOneChecked = (): boolean => {
    const checkedIds = getCheckedApprovalIds();

    if (checkedIds.length > 0) {
      methodsContext.clearErrors('specialOrderApprovalConsents');
      return true;
    }

    pendingApprovals.forEach((soa) => {
      methodsContext.setError(`specialOrderApprovalConsents.${soa.id}`, {
        type: 'manual',
      });
    });

    const firstPendingId = pendingApprovals[0]?.id;
    if (firstPendingId) {
      document
        .getElementById(`sorConsentGiven-${firstPendingId}`)
        ?.parentElement?.scrollIntoView({
          behavior: 'smooth',
          block: 'center',
        });
    }

    return false;
  };

  useEffect(() => {
    const hasAnyChecked = Object.values(checkedCount ?? {}).some(Boolean);
    const hasErrors =
      !!methodsContext.formState.errors.specialOrderApprovalConsents;

    if (hasAnyChecked && hasErrors) {
      methodsContext.clearErrors('specialOrderApprovalConsents');
    }
  }, [checkedCount]);

  const onDisapproveSORSuccessHandler = (close: () => void) => {
    toast.success(
      `You have successfully disapproved Special Order Request${
        isPlural ? 's' : ''
      }.${isUserDealerOrSalesRep ? ' Please collaborate your changes.' : ''}`
    );

    dispatch(setCurtainTab(CurtainTabsEnum.COLLABORATION_TAB));

    if (close) {
      close();
      UtilService.onPopupClose();
    }
  };

  const onRequstChangesSOR = (close: () => void) => {
    const checkedIds = getCheckedApprovalIds();
    const idsToDisapprove =
      checkedIds.length > 0
        ? checkedIds
        : pendingApprovals.map((soa) => soa.id);

    if (idsToDisapprove.length > 0) {
      dispatch(
        disapproveSpecialOrderApproval(
          {
            orderId,
            specialOrderApprovalIds: idsToDisapprove,
          },
          () => onDisapproveSORSuccessHandler(close)
        )
      );
    }
  };

  const onApproveSORSuccessHandler = (close: () => void) => {
    toast.success(
      `You have successfully approved Special Order Request${
        isPlural ? 's' : ''
      }.`
    );

    if (close) {
      close();
      UtilService.onPopupClose();
    }
  };

  const onApproveSOR = (close: () => void) => {
    const checkedIds = getCheckedApprovalIds();

    if (checkedIds.length > 0) {
      dispatch(
        approveSpecialOrderApproval(
          {
            orderId,
            specialOrderApprovalIds: checkedIds,
          },
          () => onApproveSORSuccessHandler(close)
        )
      );
    }
  };

  const latestAck = useSelector(
    (state: RootState) => state.ACKReducer.latestACK
  );

  return (
    <DealerSORActionsContainer
      flex
      middle
      between
      {...testId(ACKTestContainerEnum.DEALER_SOR_ACTIONS)}
    >
      {canApproveOrDisapproveOnBehalfOfDealer && !latestAck && (
        <ModalWrapper
          modal
          lockScroll
          onClose={UtilService.onPopupClose}
          onOpen={UtilService.onPopupOpen}
          trigger={
            <ButtonSecondary
              {...testId(ACKTestButtonEnum.SPECIAL_ORDER_APPROVAL_SEND_BTN)}
              disabled={
                order?.status?.id === OrderStatusEnums.Canceled ||
                order?.status?.id === OrderStatusEnums.Invoiced
              }
            >
              Send Special Order Approval
            </ButtonSecondary>
          }
        >
          {(close: () => void) => (
            <Modal close={close} noHeader>
              <SpecialOrderApprovalModal close={close} />
            </Modal>
          )}
        </ModalWrapper>
      )}

      <Wrapper flex middle mlAuto>
        <ConfirmationModal
          title={
            canApproveOrDisapproveOnBehalfOfDealer
              ? `Canceling request${isPlural ? 's' : ''}`
              : `Special Order Request${isPlural ? 's' : ''}`
          }
          buttonText={
            canApproveOrDisapproveOnBehalfOfDealer ? 'Yes' : 'Disapprove'
          }
          htmlMessage={
            canApproveOrDisapproveOnBehalfOfDealer
              ? `Are you sure you want to cancel ${
                  isPlural ? 'these Special Orders' : 'this Special Order'
                } <span style="font-weight: 600;">on behalf of the dealer</span>?`
              : `Are you sure you want to disapprove Special Order Request${
                  isPlural ? 's' : ''
                }? <br/><br/> <span style="color: ${lynch}; font-weight: 600;">Reminder:</span> State your reasons in the collaboration window.`
          }
          confirmBgColor={
            canApproveOrDisapproveOnBehalfOfDealer ? anzac : undefined
          }
          confirm={onRequstChangesSOR}
          opened={requestChangesSORModalOpened}
          cancel={() => setRequestChangesSORModalOpened(false)}
        />

        <ButtonPrimary
          {...testId(ACKTestButtonEnum.DISAPPROVE_SOR_BTN)}
          onClick={() => {
            if (validateAtLeastOneChecked()) {
              setRequestChangesSORModalOpened(true);
            }
          }}
        >
          Request Changes
        </ButtonPrimary>

        <Spacer w="24px" />

        <ConfirmationModal
          title={
            canApproveOrDisapproveOnBehalfOfDealer
              ? `Approve Special Order${isPlural ? 's' : ''}`
              : `Special Order Request${isPlural ? 's' : ''}`
          }
          buttonText="Approve"
          message={
            canApproveOrDisapproveOnBehalfOfDealer
              ? `Are you sure you want to approve ${
                  isPlural ? 'these Special Orders' : 'this Special Order'
                } on behalf of the dealer?`
              : `Are you sure you want to approve Special Order Request${
                  isPlural ? 's' : ''
                }?`
          }
          confirm={onApproveSOR}
          cancel={() => setApproveSORModalOpened(false)}
          confirmBgColor={
            canApproveOrDisapproveOnBehalfOfDealer ? anzac : undefined
          }
          opened={approveSORModalOpened}
        />

        <ButtonPrimary
          bgColor={canApproveOrDisapproveOnBehalfOfDealer ? anzac : undefined}
          {...testId(ACKTestButtonEnum.APPROVE_SOR_BTN)}
          onClick={() => {
            if (validateAtLeastOneChecked()) {
              setApproveSORModalOpened(true);
            }
          }}
        >
          Approve Special Order
        </ButtonPrimary>
      </Wrapper>
    </DealerSORActionsContainer>
  );
};

export default DealerSORActions;
